import { supabase } from './client'
import type { AuthError, User, Session } from '@supabase/supabase-js'

export interface AuthResult {
  user: User | null
  session: Session | null
  error: AuthError | null
}

export interface SignUpResult extends AuthResult {
  confirmationRequired: boolean
}

/**
 * Sign in with email and password
 */
export async function signIn(
  email: string,
  password: string,
  captchaToken?: string
): Promise<AuthResult> {
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password,
    options: captchaToken ? { captchaToken } : undefined,
  })

  return {
    user: data.user,
    session: data.session,
    error,
  }
}

/**
 * Sign up with email and password
 * Basejump will automatically create an account for the user on first login
 */
export async function signUp(
  email: string,
  password: string,
  metadata?: {
    displayName?: string
    orgName?: string
  },
  captchaToken?: string
): Promise<SignUpResult> {
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: {
      data: {
        display_name: metadata?.displayName,
        org_name: metadata?.orgName,
      },
      ...(captchaToken ? { captchaToken } : {}),
    },
  })

  // Check if email confirmation is required
  const confirmationRequired =
    data.user !== null &&
    data.session === null &&
    data.user.identities?.length === 0

  return {
    user: data.user,
    session: data.session,
    error,
    confirmationRequired:
      confirmationRequired || (data.user !== null && data.session === null),
  }
}

/**
 * Sign out the current user
 */
export async function signOut(): Promise<{ error: AuthError | null }> {
  const { error } = await supabase.auth.signOut()
  return { error }
}

/**
 * Get the current session
 */
export async function getSession(): Promise<{
  session: Session | null
  error: AuthError | null
}> {
  const { data, error } = await supabase.auth.getSession()
  return {
    session: data.session,
    error,
  }
}

/**
 * Get the current user
 */
export async function getUser(): Promise<{
  user: User | null
  error: AuthError | null
}> {
  const { data, error } = await supabase.auth.getUser()
  return {
    user: data.user,
    error,
  }
}

/**
 * Subscribe to auth state changes
 */
export function onAuthStateChange(
  callback: (event: string, session: Session | null) => void
) {
  const {
    data: { subscription },
  } = supabase.auth.onAuthStateChange((event, session) => {
    callback(event, session)
  })

  return subscription
}

/**
 * Request password reset email
 */
export async function resetPassword(
  email: string,
  captchaToken?: string
): Promise<{ error: AuthError | null }> {
  const { error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: `${window.location.origin}/auth/reset-password`,
    ...(captchaToken ? { captchaToken } : {}),
  })
  return { error }
}

/**
 * Update user password (for logged in users or after reset)
 */
export async function updatePassword(
  newPassword: string
): Promise<{ error: AuthError | null }> {
  const { error } = await supabase.auth.updateUser({
    password: newPassword,
  })
  return { error }
}
